import { useState } from "react";
import { toast } from "sonner";
import { Zap, ShieldCheck, Mail } from "lucide-react";
import { Switch } from "./ui/switch";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";
import api, { formatApiError } from "../lib/api";

export default function AutoFixToggle({ connection, onChange, showDigestPreferences = true }) {
  const [enabled, setEnabled] = useState(!!connection.auto_fix_enabled);
  const [perScan, setPerScan] = useState(!!connection.digest_per_scan);
  const [weekly, setWeekly] = useState(!!connection.digest_weekly);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const available = connection.auto_fix_available === true || enabled;

  const persist = async (next) => {
    setSaving(true);
    try {
      await api.patch(`/connections/${connection.id}/auto-fix`, { enabled: next });
      setEnabled(next);
      toast.success(next ? "Auto-Fix enabled for this connection" : "Auto-Fix disabled");
      onChange && onChange(next);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const saveDigest = async (ps, wk) => {
    setPerScan(ps); setWeekly(wk);
    try {
      await api.patch(`/connections/${connection.id}/digest`, { per_scan: ps, weekly: wk });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleToggle = (next) => {
    if (next) setConfirmOpen(true); // enabling requires explicit confirmation
    else persist(false);
  };

  return (
    <>
      <div className="mt-3 flex min-h-touch items-center justify-between gap-2 border-t border-border pt-3"
           data-testid={`auto-fix-row-${connection.id}`}>
        <div className="flex items-center gap-2">
          <Zap className={`h-3.5 w-3.5 ${enabled ? "text-brand" : "text-slate-400"}`} />
          <div className="leading-tight">
            <div className="text-xs font-semibold text-slate-800">Auto-Fix</div>
            <div className="font-mono text-[10px] text-slate-400">
              {enabled ? "Safe fixes applied after each scan" : available ? "Off — every fix needs approval" : "Locked — production approval pending"}
            </div>
          </div>
        </div>
        <Switch data-testid={`auto-fix-switch-${connection.id}`} checked={enabled}
                disabled={saving || (!enabled && !available)} onCheckedChange={handleToggle} />
      </div>

      {!enabled && !available && (
        <div data-testid={`auto-fix-unavailable-${connection.id}`} className="mt-2 border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          {connection.auto_fix_unavailable_reason || "Auto-Fix is not available in this environment."}
        </div>
      )}

      {enabled && showDigestPreferences && (
        <div data-testid={`digest-prefs-${connection.id}`} className="mt-2 flex flex-col gap-2 border-t border-dashed border-border pt-2">
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
            <Mail className="h-3 w-3" /> Email digest
          </div>
          <label className="flex min-h-touch items-center justify-between gap-2">
            <span className="text-xs text-slate-600">After each scan</span>
            <Switch data-testid={`digest-per-scan-${connection.id}`} checked={perScan}
                    onCheckedChange={(v) => saveDigest(v, weekly)} />
          </label>
          <label className="flex min-h-touch items-center justify-between gap-2">
            <span className="text-xs text-slate-600">Weekly rollup</span>
            <Switch data-testid={`digest-weekly-${connection.id}`} checked={weekly}
                    onCheckedChange={(v) => saveDigest(perScan, v)} />
          </label>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="auto-fix-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-brand" /> Enable Auto-Fix?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>
                  When ON, CleanIQ will automatically apply <strong>safe field normalizations</strong>
                  {" "}(such as name casing and phone formatting) to <strong>{connection.label}</strong> at
                  the end of every scan — without a manual approval step.
                </p>
                <p className="text-slate-500">
                  Deletions and duplicate merges <strong>always</strong> require your manual approval.
                  Every automatic change is recorded in the audit log and can be reverted. You can turn
                  this off at any time.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="auto-fix-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="auto-fix-confirm-enable"
                               className="bg-slate-900 hover:bg-brand"
                               onClick={() => persist(true)}>
              Enable Auto-Fix
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
